import Vue, { ref } from 'vue';
import PartialPaymentPopup from '@ve/components/partialPayment/partialPaymentPopup/desktop/PartialPaymentPopup.vue';

import { propsDecorator } from '@/js/common/Domain/Service/mixins/decorators';

import HotelCategoriesRepository from '@/js/common/Domain/Repository/HotelCategories';
import tourHunterService from '@/js/common/Domain/Service/tourHunter';
import TourHunterSubscribeView from '@/js/desktop/components/search/tourHunterSubscribe/TourHunterSubscribe';
import firstPaymentDefinitionService from '@/js/common/Domain/Service/firstPaymentDefinition';
import getTourProductFirstPaymentDefinitions
    from '@/js/common/Domain/Service/hotelSearch/services/getTourProductFirstPaymentDefinitions';

import HotelPageModel from '@/js/desktop/components/hotel/model/HotelPageModel';

import getStore from '@ve/services/getStore/getStore';
import HotelMinPriceButtonDesktop
    from '@ve/components/hotel/hotelMinPriceButton/desktop/HotelMinPriceButton.vue';

import CashbackHotelCardBanner from '@/js/desktop/components/cashbackHotelCardBanner/cashbackHotelCardBanner';
import PartialPaymentBannerForHotelPage
    from '@ve/components/partialPayment/partialPaymentBannerForHotelPage/desktop/PartialPaymentBannerForHotelPage.vue';
import HotelTrackingService from '@ve/pages/hotel/analytics/yandexAnalytics/hotelTrackingService';
import hotelPageMailAnalyticsSingleton from '@ve/pages/hotel/analytics/mailAnalytics/HotelPageMailAnalyticsSingleton';
import abTests from '@/js/common/Domain/Service/abTests';

import { HotelDescriptionRepository, HOTEL_DESCRIPTION_STORE, HotelDescription } from '@entities/hotelDescription';
import TourSearchView from './tourSearch/TourSearch';
import MapHotelsSearchView from './MapHotelsSearch/MapHotelsSearch';
import ThFavoritesHotelView from './thFavoritesHotel/thFavoritesHotel';
import ShareDgView from './shareDg/shareDg';
import PartialPaymentTooltipView from './pageTopBlock/partialPaymentTooltip/PartialPaymentTooltip';

@propsDecorator({
    subview: {
        comments: null,
        popovers: null,
        tourSearch: null,
    },
    events: {
        'click .viewHotelAboutLink': 'gotoHotelAbout',
        'click .allReviewsLink': 'allReviewsLinkClickHandler',
        'click .viewMapLink': 'viewMapLinkClickHandler',
        'click .hotelDetails__about-more': 'hotelDescriptionClickHandler',
        'click .formControl.hotel': 'initSearchFormRegionMetric',
    },
    bindings: {
        '.tourHunterTop .tourHunterPopover2 .tourHunterEmail': 'text:customerEmail',

        '.addToFavorites': "classes:{'active': all(minPriceBySearchDates, thereIsToursOffers)}, attr: {disabled: any(not(minPriceBySearchDates), not(thereIsToursOffers))}",
        '.js-wrap-for-subscribeBannerTemplate': 'toggle:all(minPriceBySearchDates, not(subscribed))',
        '.js-cashbackHotelPageBanner': 'toggle:showHotelCashbackBanner',
    },
    bindingFilters: {
        equal(modelField, value) {
            return modelField === value;
        },
        formatNumber(number) {
            const intNumber = +number;
            if (_.isNumber(intNumber)) {
                return intNumber.formatNumber();
            }
            return null;
        },
    },
})

export default class HotelPageView extends Backbone.Epoxy.View {
    initialize(options) {
        this.isPopupVisible = ref(false);
        this.partialPaymentPopup = false;
        this.hotelTrackingService = new HotelTrackingService();
        this.dataLayerAnalytics = options.dataLayerAnalytics;
        this.initModel();
        this.initTourSearch();
        this.initModelEvents(options);

        this.initVuexStore();

        this.initSubViews();

        this.initDOMEvent();

        /* scroll to reviews if url param*/
        if (document.location.hash.indexOf('toReviews=true') !== -1) {
            setTimeout(this.gotoAllReviews, 3000);
        }
        /* scroll to map if url param*/
        if (document.location.hash.indexOf('toMap=true') !== -1) {
            setTimeout(this.gotoMap, 3000);
        }
        /* set additionalFilters.isExclusive if url params */
        if (document.location.hash.indexOf('isExclusive=true') !== -1) {
            this.model.get('tourSearchModel').get('filterCriteria').set('isExclusive', true);
        }
    }

    initModel() {
        this.model = new HotelPageModel();
    }

    initTourSearch() {
        this.subview.tourSearch = new TourSearchView({
            el: '.hotelSerpContainer__tab-content#tab-content-tours',
            parentView: this,
        });
    }

    initModelEvents(options) {
        this.model.set('tourSearchModel', this.subview.tourSearch.model);
        this.model.set('hotelInfo', options.hotelInfo ? options.hotelInfo : null);

        this.model
            .get('tourSearchModel')
            ?.on('change:syncTourProducts', this.syncTourProductsChanged.bind(this));

        this.model
            .get('tourSearchModel')
            ?.on('change:syncTourProducts', this.updateBannerData.bind(this));

        this.model.get('tourSearchModel')
            ?.get('filterCriteria')
            ?.on('change:productType', this.updateBannerData.bind(this));

        this.model.on('change:minPriceBySearchDates', this.updateMinPriceProductHandler.bind(this));
    }

    initVuexStore() {
       this.initHotelDescriptionStore();
    }

    initHotelDescriptionStore() {
        const store = getStore();
        store.registerModule(HOTEL_DESCRIPTION_STORE, HotelDescriptionRepository);
        this.transferDataFromWindowToVuex();
    }

    transferDataFromWindowToVuex() {
        const store = getStore();
        const descriptionList = window.appConfig.hotelDescriptionList;
        store.commit(`${HOTEL_DESCRIPTION_STORE}/setHotelDescriptionList`, descriptionList);
    }

    initVueComponents() {
        getStore({
            name: 'mapForHotelMinPriceButton',
            backboneMap: [{
                fields: [{
                    price: 'minPriceBySearchDates',
                }],
                model: this.model,
            }],
        });

        getStore({
            name: 'mapForCountryConditions',
            backboneMap: [
                {
                    fields: [{
                        countries: {
                            field: 'searchData',
                            action: (model, searchData) => searchData?.get('countries')?.pluck('id'),
                        },
                    }],
                    model: this.model.get('tourSearchModel'),
                },
            ],
        });

        this.initHotelMinPriceButton();
        this.initHotelDescription();
    }

    initSearchFormRegionMetric() {
        this.hotelTrackingService.searchFormRegionClick();
    }

    initHotelDescription() {
        const store = getStore();
        const isNewHotelDescription = store.getters[`${HOTEL_DESCRIPTION_STORE}/isNewHotelDescription`]

        if (isNewHotelDescription) {
            new Vue({
                name: 'HotelDescription',
                render: (h) => h(HotelDescription, {}),
                store,
            }).$mount('.js-hotel-description-info');
        }

        this.hotelTrackingService.trackingHotelDescription({
            isHotelViewNew: isNewHotelDescription,
            isHotelViewOld: !isNewHotelDescription,
        });
    }

    initHotelMinPriceButton() {
        const store = getStore();
        new Vue({
            name: 'HotelMinPriceButton',
            render: (h) => h(HotelMinPriceButtonDesktop, {
                nativeOn: {
                    click: () => {
                        this.subview.tourSearch.formSubmit();
                        this.gotoSearchResults();
                        this.hotelTrackingService.headPriceButtonClick();
                    },
                },
                props: {
                    pageMinPrice: window.appConfig.hotelInfo.minPrice,
                },
            }),
            store,
        }).$mount('#hotelMinPriceButtonContainer');
    }

    initSubViews() {
        this.initVueComponents();

        this.subview.mapHotelsSearch = new MapHotelsSearchView({
            el: '.hotelsMap',
            parentView: this,
        });

        new ThFavoritesHotelView({
            model: this.model.get('tourSearchModel'),
            searchPageModel: this.model,
            el: $('.addToFavorites', this.$el),
        });

        new ShareDgView({
            $openLink: $('.js-open-share-dg'),
        });

        new PartialPaymentTooltipView({
            model: this.model.get('tourSearchModel'),
            el: '.price-block',
        });

        this.tourHunterSubscribeInit();
        this.initCashbackHotelPageBanner();
    }

    initCashbackHotelPageBanner() {
        new CashbackHotelCardBanner({
            el: $('.js-cashbackHotelPageBanner'),
            addType: 'append',
            tailPosition: 'bottom-center',
            width: 'full',
            rounded: 'top',
            productType: 'tour',
            page: 'hotelPage',
        });
    }

    tourHunterSubscribeInit() {
        let tourHunterSubscribeBanner;
        const updateSubscribeState = () => {
            const tourCriteria = this.model.get('tourSearchModel').get('searchData').clone();

            tourCriteria.set('hotelCategories', new HotelCategoriesRepository());

            const tourCriteriaIsSubscribed = tourHunterService.criteriaIsSubscribed(tourCriteria);

            if (!tourHunterSubscribeBanner.viewModel.get('currentSubscribeBanner')) {
                this.model.set('subscribed', tourCriteriaIsSubscribed);
            } else {
                tourHunterSubscribeBanner.viewModel.set('currentSubscribeBanner', false);
            }
        };

        TourHunterSubscribeView.prototype.getTourCriteria = () => {
            const tourCriteria = this.subview.tourSearch.searchFormModel.getTourCriteria().clone();

            tourCriteria.set('hotelCategories', new HotelCategoriesRepository());

            return tourCriteria;
        };

        tourHunterSubscribeBanner = new TourHunterSubscribeView({
            trackingService: this.hotelTrackingService,
            el: '.js-wrap-for-subscribeBannerTemplate',
            searchPageModel: this.model.get('tourSearchModel'),
            subscribeStateHTML: 'Добавьте отель в Турхантер, и мы пришлем вам письмо,<br>когда цена на туры по этому отелю снизится.',
        });

        tourHunterService.getActiveSubscribes().on('add remove reset', updateSubscribeState);

        this.model.get('tourSearchModel').on('change:searchData', () => {
            tourHunterSubscribeBanner.reset();
            updateSubscribeState();
        });
    }

    initPartialPaymentPopup(props) {
        const popup = document.createElement('div');
        popup.className = 'partial-payment-popup';
        document.getElementsByTagName('body')[0].appendChild(popup);
        this.partialPaymentPopup = new Vue({
            name: 'partial-payment-popup',
            render: (h) => h(PartialPaymentPopup, {
                    props: {
                        ...props,
                        'hotel-page-product': true,
                        isPopupVisible: this.isPopupVisible,
                    },
                }),
        }).$mount('.partial-payment-popup');
    }

    openPartialPaymentPopup(props) {
        if (!this.partialPaymentPopup) {
            this.initPartialPaymentPopup(props);
        }

        this.isPopupVisible.value = true;
    }

    renderPartialPaymentBanner() {
        const productType = this.model?.get('tourSearchModel')?.get('filterCriteria')?.get('productType');

        this.partialPaymentBannerForHotelPage = new Vue({
            name: 'PartialPaymentBannerForHotelPage',
            render: (h) => h(PartialPaymentBannerForHotelPage, {
                props: {
                    countryName: this.model.get('hotelInfo')?.countryName || '',
                    products: this.syncTourProducts.toJSON(),
                    productType,
                    hotelPageProduct: true,
                },
                on: {
                    'open-popup': (event) => {
                        this.openPartialPaymentPopup(event);
                    },
                },
            }),
        }).$mount();

        $('.partialPaymentBanner_forHotelPage').empty().append(this.partialPaymentBannerForHotelPage.$el);
    }

    get syncTourProducts() {
        return this.model
            ?.get('tourSearchModel')
            ?.get('syncTourProducts') || [];
    }

    syncTourProductsChanged() {
        const syncTourProducts = this?.syncTourProducts;

        if (syncTourProducts?.length > 0) {
            this.toggleLessPlacesLabel((syncTourProducts.at(0)?.get('tour')?.get('lessPlaces')));
        }
    }

    toggleLessPlacesLabel(status) {
        const $lessPlacesLabel = this.$('.hotelLabelLessPlaces');

        if (status) {
            $lessPlacesLabel.show();
        } else {
            $lessPlacesLabel.hide();
        }
    }

    togglePanelVisibilityInit() {
        const hotelPageHeaderFormContainer = $('.hotel-page-header-form__container');

        $('.showToggle', hotelPageHeaderFormContainer).on('click', () => {
            hotelPageHeaderFormContainer.toggleClass('minimized');
        });
    }

    gotoSearchResults() {
        $('html,body').animate({
            scrollTop: $('.hotelContent').offset().top - 100,
        }, 500);
    }

    gotoHotelAbout() {
        $('html,body').animate({
            scrollTop: $('.hotelSectionAbout').offset().top - 100,
        }, 500);
    }

    gotoAllReviews() {
        $('html, body').animate({ scrollTop: $('.reviewsTabs').offset().top - 100 }, 500);
    }

    allReviewsLinkClickHandler() {
        this.gotoAllReviews();
        this.hotelTrackingService.allReviewsClick();
    }

    gotoMap(e) {
        e?.preventDefault();
        e?.stopPropagation();
        $('html, body').animate({ scrollTop: $('.hotelsMap').offset().top - 100 }, 500);
        if (!$('#showMapContainer:visible').length) {
            $('#openMap').click();
        }
    }

    viewMapLinkClickHandler() {
        this.gotoMap();
        this.hotelTrackingService.viewMapLinkClick();
    }

    gotoHotelDescription(e) {
        e.preventDefault();
        e.stopPropagation();
        $('html, body').animate({ scrollTop: $('.aboutHotel').offset().top - 100 }, 500);
    }

    hotelDescriptionClickHandler(e) {
        this.gotoHotelDescription(e);
        this.hotelTrackingService.hotelDescriptionClick();
    }

    updateBannerData() {
        const tourProducts = this.syncTourProducts;

        if (tourProducts.length > 0) {
            const firstPaymentDefinitions = getTourProductFirstPaymentDefinitions(tourProducts);

            // запоминаем текущие данные предоплат
            firstPaymentDefinitionService.setData(firstPaymentDefinitions);

            if (firstPaymentDefinitions?.length) {
                this.renderPartialPaymentBanner();
            }
        }
    }

    updateMinPriceProductHandler() {
        const minPriceBySearchDates = this.model.get('minPriceBySearchDates');
        const thereIsToursOffers = this.model.get('thereIsToursOffers');

        if (thereIsToursOffers) {
            hotelPageMailAnalyticsSingleton.updatePrice(minPriceBySearchDates);
        }
    }

    hotelPageOnloadAnalytics() {
        const {
            id,
            minPrice,
        } = this.model.get('hotelInfo');

        const itIsRailwaySearch = document.location.hash.indexOf('productType=railway') !== -1;
        const itIsHotelOfferSearch = document.location.hash.indexOf('productType=hotelOffer') !== -1;

        if (itIsRailwaySearch || itIsHotelOfferSearch) {
            return;
        }

        hotelPageMailAnalyticsSingleton.setHotelPageTimeoutViewPageEvent(minPrice, id);
    }

    searchScrollHandler() {
        const loadingInProgress = this.subview.tourSearch.model.get('loadingInProgress');
        const beforeToursLoading = this.model.get('tourSearchModel')?.get('beforeToursLoading');

        document.removeEventListener('scroll', this.scrollHandlerWithContext);

        if (loadingInProgress || !beforeToursLoading) return;

        this.subview.tourSearch.formSubmit();
    }

    initSearchOnScroll() {
        const beforeToursLoading = this.model.get('tourSearchModel')?.get('beforeToursLoading');

        if (!beforeToursLoading) return;

        this.scrollHandlerWithContext = () => this.searchScrollHandler();
        document.addEventListener('scroll', this.scrollHandlerWithContext);
    }

    initDOMEvent() {
        this.togglePanelVisibilityInit();
        this.hotelPageOnloadAnalytics();
        this.initSearchOnScroll();
    }
}
